import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, MapPin, RefreshCw, Plus, Trash2, Home, Building, 
  TrendingUp, Users, Calendar, Euro, Loader2, CheckCircle2, XCircle,
  ExternalLink, ChevronDown, ChevronUp, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ListingsModal({ zone, onClose }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchListings();
  }, [zone.zone_id]);
  
  const fetchListings = async () => {
    try {
      const response = await axiosInstance.get(`${API}/market-zones/${zone.zone_id}/listings`, { withCredentials: true });
      setListings(response.data.airbnb_listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-[#0a0a0f] border border-cyan-500/30 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-white/10 flex justify-between items-center">
          <h3 className="text-lg font-bold text-white">
            Annonces Airbnb - {zone.name}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400">
            ✕
          </Button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
            </div>
          ) : listings.length === 0 ? (
            <p className="text-center text-slate-400 py-8">
              Aucune annonce stockée. Cliquez sur "Actualiser" pour récupérer les annonces.
            </p>
          ) : (
            <div className="grid gap-3">
              {listings.map((listing, idx) => (
                <div key={idx} className="bg-black/40 rounded-lg p-3 border border-white/5 hover:border-cyan-500/30 transition-colors">
                  <div className="flex gap-4">
                    {listing.image && (
                      <img 
                        src={listing.image} 
                        alt={listing.title}
                        className="w-24 h-24 object-cover rounded-lg shrink-0"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold truncate">{listing.title || 'Sans titre'}</h4>
                      <p className="text-slate-400 text-sm truncate">{listing.subtitle || listing.room_type}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-cyan-400 font-bold">{listing.price_per_night}€/nuit</span>
                        {listing.rating && (
                          <span className="text-yellow-400 flex items-center gap-1">
                            <Star className="h-3 w-3 fill-current" />
                            {listing.rating}
                          </span>
                        )}
                        {listing.reviews_count > 0 && (
                          <span className="text-slate-500">({listing.reviews_count} avis)</span>
                        )}
                      </div>
                      {listing.url && (
                        <a 
                          href={listing.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 text-sm mt-2"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Voir sur Airbnb
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ZoneCard({ zone, onRefresh, onDelete, refreshing }) {
  const [adults, setAdults] = useState(2);
  const [showListings, setShowListings] = useState(false);
  
  const formatDate = (isoString) => {
    if (!isoString) return 'Jamais';
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div className="glass-panel rounded-lg p-5 border border-cyan-500/20">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <MapPin className="h-5 w-5 text-cyan-400" />
              {zone.name}
            </h3>
            <p className="text-sm text-slate-400">
              {zone.city} - {zone.postal_code} • Rayon {zone.radius_km}km
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(zone.zone_id)}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Airbnb Data */}
        <div className="bg-black/30 rounded-lg p-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-cyan-400 flex items-center gap-2">
              <Building className="h-4 w-4" />
              Airbnb
            </h4>
            {zone.last_airbnb_update && (
              <span className="text-xs text-slate-500">
                {formatDate(zone.last_airbnb_update)}
              </span>
            )}
          </div>
          
          {zone.airbnb_data ? (
            <>
              <p className="text-xs text-slate-400 mb-2">
                Prix pour <span className="text-cyan-400 font-semibold">{zone.airbnb_data.adults || 2} adultes</span> par nuit
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-400">Prix moyen/nuit:</span>
                  <span className="text-green-400 font-semibold ml-2">{zone.airbnb_data.avg_price_per_night}€</span>
                </div>
                <div>
                  <span className="text-slate-400">Min-Max:</span>
                  <span className="text-white ml-2">{zone.airbnb_data.min_price_per_night}€ - {zone.airbnb_data.max_price_per_night}€</span>
                </div>
                <div>
                  <span className="text-slate-400">Revenu/mois (65%):</span>
                  <span className="text-cyan-400 font-semibold ml-2">{zone.airbnb_data.estimated_monthly_income}€</span>
                </div>
                <div>
                  <span className="text-slate-400">Annonces:</span>
                  <span className="text-white ml-2">{zone.airbnb_data.listings_count}</span>
                </div>
              </div>
              {/* Data for 4 adults if available */}
              {zone.airbnb_data_4 && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-xs text-slate-400 mb-2">
                    Prix pour <span className="text-purple-400 font-semibold">4 adultes</span> par nuit
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-400">Prix moyen:</span>
                      <span className="text-purple-400 font-semibold ml-2">{zone.airbnb_data_4.avg_price_per_night}€</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Revenu/mois:</span>
                      <span className="text-purple-400 font-semibold ml-2">{zone.airbnb_data_4.estimated_monthly_income}€</span>
                    </div>
                  </div>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowListings(true)}
                className="mt-2 text-cyan-400 hover:text-cyan-300 text-xs"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Voir les {zone.airbnb_data.listings_count} annonces
              </Button>
            </>
          ) : (
            <p className="text-slate-500 text-sm italic">Aucune donnée - Cliquez sur Actualiser</p>
          )}
        </div>

      {/* LeBonCoin Data */}
      <div className="bg-black/30 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-orange-400 flex items-center gap-2">
            <Home className="h-4 w-4" />
            Location Traditionnelle
            {zone.leboncoin_data?.source === 'insee_estimate' && (
              <span className="text-xs text-yellow-500 bg-yellow-500/10 px-2 py-0.5 rounded">(Estimation INSEE)</span>
            )}
          </h4>
          {zone.last_leboncoin_update && (
            <span className="text-xs text-slate-500">
              {formatDate(zone.last_leboncoin_update)}
            </span>
          )}
        </div>
        
        {zone.leboncoin_data ? (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-slate-400">Loyer moyen:</span>
              <span className="text-orange-400 font-semibold ml-2">{zone.leboncoin_data.avg_rent_monthly}€/mois</span>
            </div>
            <div>
              <span className="text-slate-400">Min-Max:</span>
              <span className="text-white ml-2">{zone.leboncoin_data.min_rent_monthly}€ - {zone.leboncoin_data.max_rent_monthly}€</span>
            </div>
            {zone.leboncoin_data.price_per_m2 && (
              <div>
                <span className="text-slate-400">Prix/m²:</span>
                <span className="text-white ml-2">{zone.leboncoin_data.price_per_m2}€/m²</span>
              </div>
            )}
            <div>
              <span className="text-slate-400">
                {zone.leboncoin_data.source === 'insee_estimate' ? 'Source:' : 'Annonces:'}
              </span>
              <span className="text-white ml-2">
                {zone.leboncoin_data.source === 'insee_estimate' 
                  ? zone.leboncoin_data.region 
                  : zone.leboncoin_data.listings_count}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-slate-500 text-sm italic">Aucune donnée - Cliquez sur Actualiser</p>
        )}
      </div>

      {/* Refresh Controls */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Label htmlFor={`adults-${zone.zone_id}`} className="text-xs text-slate-400">Adultes:</Label>
          <Input
            id={`adults-${zone.zone_id}`}
            type="number"
            min="1"
            max="10"
            value={adults}
            onChange={(e) => setAdults(parseInt(e.target.value) || 2)}
            className="w-16 h-8 text-sm bg-black/50 border-slate-700"
          />
        </div>
        
        <Button
          onClick={() => onRefresh(zone.zone_id, true, true, adults)}
          disabled={refreshing === zone.zone_id}
          className="flex-1 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white"
        >
          {refreshing === zone.zone_id ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Scraping en cours...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser (Airbnb + Location)
            </>
          )}
        </Button>
      </div>
    </div>
    
    {/* Listings Modal */}
    {showListings && (
      <ListingsModal zone={zone} onClose={() => setShowListings(false)} />
    )}
    </>
  );
}

export default function MarketDataPage({ user }) {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // New zone form
  const [newZone, setNewZone] = useState({
    name: '',
    city: '',
    postal_code: '',
    radius_km: 10
  });

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await axiosInstance.get(`${API}/market-zones`, { withCredentials: true });
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
      toast.error('Erreur lors du chargement des zones');
    } finally {
      setLoading(false);
    }
  };

  const handleAddZone = async (e) => {
    e.preventDefault();
    
    if (!newZone.name || !newZone.city || !newZone.postal_code) {
      toast.error('Remplis tous les champs obligatoires');
      return;
    }
    
    try {
      const response = await axiosInstance.post(`${API}/market-zones`, newZone, { withCredentials: true });
      setZones([...zones, response.data]);
      setNewZone({ name: '', city: '', postal_code: '', radius_km: 10 });
      setShowAddForm(false);
      toast.success(`Zone "${newZone.name}" créée !`);
    } catch (error) {
      console.error('Error creating zone:', error);
      toast.error('Erreur lors de la création de la zone');
    }
  };

  const handleRefresh = async (zoneId, scrapeAirbnb, scrapeLeboncoin, adults) => {
    setRefreshing(zoneId);
    
    try {
      const response = await axiosInstance.post(
        `${API}/market-zones/${zoneId}/refresh`,
        { scrape_airbnb: scrapeAirbnb, scrape_leboncoin: scrapeLeboncoin, adults },
        { withCredentials: true }
      );
      
      // Update local state
      await fetchZones();
      
      const updates = response.data.updates || [];
      const successes = updates.filter(u => u.status === 'success').length;
      const failures = updates.filter(u => u.status === 'failed' || u.status === 'no_data').length;
      
      if (successes > 0) {
        toast.success(`Données mises à jour ! (${successes}/${updates.length})`);
      }
      if (failures > 0) {
        toast.warning(`Certaines données n'ont pas pu être récupérées`);
      }
    } catch (error) {
      console.error('Error refreshing zone:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setRefreshing(null);
    }
  };

  const handleDelete = async (zoneId) => {
    if (!window.confirm('Supprimer cette zone de marché ?')) return;
    
    try {
      await axiosInstance.delete(`${API}/market-zones/${zoneId}`, { withCredentials: true });
      setZones(zones.filter(z => z.zone_id !== zoneId));
      toast.success('Zone supprimée');
    } catch (error) {
      console.error('Error deleting zone:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="min-h-screen bg-[#030305] relative">
      <div className="cyber-grid-bg"></div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 backdrop-blur-xl bg-black/40">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour
              </Button>
              <h1 className="text-xl font-bold font-['Orbitron'] tracking-wider text-white">
                DONNÉES <span className="text-cyan-400">MARCHÉ</span>
              </h1>
              <Button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-cyan-500 hover:bg-cyan-400 text-black"
              >
                <Plus className="mr-2 h-4 w-4" />
                Ajouter Zone
              </Button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          {/* Info Banner */}
          <div className="glass-panel rounded-lg p-4 mb-6 border border-cyan-500/30">
            <div className="flex items-start gap-3">
              <TrendingUp className="h-6 w-6 text-cyan-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="text-white font-semibold mb-1">Données de marché pour vos simulations</h2>
                <p className="text-slate-400 text-sm">
                  Configurez vos zones de marché pour obtenir les prix réels Airbnb et Location traditionnelle.
                  Ces données seront utilisées automatiquement dans vos simulations.
                </p>
              </div>
            </div>
          </div>

          {/* Add Zone Form */}
          {showAddForm && (
            <div className="glass-panel rounded-lg p-6 mb-6 border border-cyan-500/30">
              <h3 className="text-lg font-semibold text-white mb-4">Nouvelle Zone de Marché</h3>
              <form onSubmit={handleAddZone} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="name" className="text-slate-300 text-sm">Nom de la zone *</Label>
                  <Input
                    id="name"
                    value={newZone.name}
                    onChange={(e) => setNewZone({...newZone, name: e.target.value})}
                    placeholder="Ex: La Loupe"
                    className="bg-black/50 border-slate-700 text-white mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="text-slate-300 text-sm">Ville *</Label>
                  <Input
                    id="city"
                    value={newZone.city}
                    onChange={(e) => setNewZone({...newZone, city: e.target.value})}
                    placeholder="Ex: La Loupe"
                    className="bg-black/50 border-slate-700 text-white mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code" className="text-slate-300 text-sm">Code Postal *</Label>
                  <Input
                    id="postal_code"
                    value={newZone.postal_code}
                    onChange={(e) => setNewZone({...newZone, postal_code: e.target.value})}
                    placeholder="Ex: 28240"
                    className="bg-black/50 border-slate-700 text-white mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="radius" className="text-slate-300 text-sm">Rayon (km)</Label>
                  <Input
                    id="radius"
                    type="number"
                    min="1"
                    max="100"
                    value={newZone.radius_km}
                    onChange={(e) => setNewZone({...newZone, radius_km: parseInt(e.target.value) || 10})}
                    className="bg-black/50 border-slate-700 text-white mt-1"
                  />
                </div>
                <div className="md:col-span-2 lg:col-span-4 flex gap-3">
                  <Button type="submit" className="bg-cyan-500 hover:bg-cyan-400 text-black">
                    <Plus className="mr-2 h-4 w-4" />
                    Créer la zone
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={() => setShowAddForm(false)}
                    className="text-slate-400"
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Zones List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
            </div>
          ) : zones.length === 0 ? (
            <div className="glass-panel rounded-lg p-12 text-center">
              <MapPin className="h-16 w-16 text-cyan-500/50 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Aucune zone de marché</h3>
              <p className="text-slate-400 mb-6">
                Ajoutez vos premières zones pour commencer à collecter les données de marché.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => {
                    setNewZone({ name: 'La Loupe', city: 'La Loupe', postal_code: '28240', radius_km: 10 });
                    setShowAddForm(true);
                  }}
                  className="bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-black"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ajouter La Loupe 28240
                </Button>
                <Button
                  onClick={() => {
                    setNewZone({ name: 'Audun-le-Tiche', city: 'Audun-le-Tiche', postal_code: '57390', radius_km: 10 });
                    setShowAddForm(true);
                  }}
                  className="bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-black"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ajouter Audun-le-Tiche 57390
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {zones.map((zone) => (
                <ZoneCard
                  key={zone.zone_id}
                  zone={zone}
                  onRefresh={handleRefresh}
                  onDelete={handleDelete}
                  refreshing={refreshing}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
